import React from 'react';
import AssemblyLibraryStore from '../stores/AssemblyLibraryStore';
import FacilityStore from '../stores/FacilityStore';
import ViewerStore from '../stores/ViewerStore';
import AssemblyLibraryActions from '../actions/AssemblyLibraryActions';
import FacilityActions from '../actions/FacilityActions';
import ViewerActions from '../actions/ViewerActions';
import * as Inovx from '@shared';
import FacilitySlideoutSessionPanel from './FacilitySlideoutSessionPanel';
import {
    SessionPanelMeasuredContent,
    SessionPanelToolbar,
    type SessionPanelRenderContext,
} from '../../shared/panels/SessionPanel';
import { FACILITY_SLIDEOUT_PANEL_IDS } from '../helpers/facilitySlideoutPanelSession';
const { useState, useEffect, useRef } = React;
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
const ASSEMBLY_LIBRARY_PANEL_ID = FACILITY_SLIDEOUT_PANEL_IDS.assemblyLibrary;

interface AssemblyLibraryBarProps { }

const AssemblyLibraryBar = React.forwardRef((props: AssemblyLibraryBarProps, ref) => {
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(FacilityStore.getSidebarOpen());
    const [assemblyLibraryBarOpen, setAssemblyLibraryBarOpen] = useState<boolean>(
        FacilityStore.getAssemblyLibrarybarOpen()
    );

    const [assemblyLibraries, setAssemblyLibraries] = useState<IAssemblyLibraryWithTemplates[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<IAssemblyTemplate | null>(ViewerStore.getAssemblyCreationTemplate()); // Track selected template
    const [assemblyLibraryClass, setAssemblyLibraryClass] = useState('');
    const [listMode, setListMode] = useState<string>('list');
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    
    const updateAssemblyLibraries = async () => {
        await AssemblyLibraryActions.getAssemblyLibrariesWithTemplates();
    };

    const toggleLibraryExpansion = async (libraryId: string) => {
        setAssemblyLibraries((prev) =>
            prev.map((library) => ({
                ...library,
                IsExpanded: library.ID === libraryId ? !library.IsExpanded : library.IsExpanded,
            }))
        );
    };

    const handleTemplateClick = (template: IAssemblyTemplate) => {
        setSelectedTemplate(template); // Store the full template object
        ViewerActions.setSelectedAssemblyTemplate(template);
    };

    const renderTemplateThumbnail = (template: IAssemblyTemplate) => {
        const isSelected = selectedTemplate?.ID === template.ID; // Check if the template is selected

        return (
            <div
                key={template.ID}
                className={`${listMode === 'list' ? 'list-item' : 'thumbnail-item'} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleTemplateClick(template)}
            >{(listMode === 'thumbnail' && isSelected) && <div className="selected-overlay"></div>}
                {listMode === 'thumbnail' && <img src={template.ThumbnailImageUrl} alt={template.Name} className="thumbnail-image" />}
                <div className={`${listMode === 'list' ? 'list-title' : 'thumbnail-title'}`}>{template.Name}</div>
            </div>
        );
    };
    const filterByName = (array: IAssemblyTemplate[], searchString: string) => {
        if (!searchString) return array; // If no search string, return the full array
        return array.filter((item: IAssemblyTemplate) =>
            item.Name.toLowerCase().includes(searchString.toLowerCase())
        );
    }
    const renderLibraryTemplate = (library: IAssemblyLibraryWithTemplates) => {
        const templates = library.Templates || [];
        const hasSearchKeyword = searchKeyword && searchKeyword.trim() !== '';
        let filteredTemplates = templates;

        // Apply search filtering if there's a keyword
        if (hasSearchKeyword) {
            filteredTemplates = filterByName(templates, searchKeyword);
        }
        const hasSearchResults = filteredTemplates.length > 0;
        let shouldShowContent = false;
        if (library.IsExpanded && library.IsExpanded !== undefined) {
            shouldShowContent = true;
        } else if (searchKeyword !== '' && hasSearchResults) {
            shouldShowContent = true;
        }
        if (!library.IsExpanded && library.IsExpanded !== undefined) {
            shouldShowContent = false;
        }

        return (
            <React.Fragment key={library.ID}> {/* Add unique key to React.Fragment */}
                {hasSearchResults && (
                    <div className="library-item">
                        <div className="library-header" onClick={() => toggleLibraryExpansion(library.ID)}>
                            <span className={`expander-icon`}>
                                <img
                                    src={`${UrlUtilities.getBaseApplicationUrl()}/images/icons/Arrow.png`}
                                    alt={''}
                                    className={`arrow-expander ${shouldShowContent ? 'expanded' : ''}`}
                                />
                            </span>
                            <span className="library-title">{library.Name}</span>
                        </div>
                        {shouldShowContent && (
                            <>
                                {templates?.length === 0 && (
                                    <div className="d-flex j-cc">{InovxLoader.t('facility:message.noTemplatesAddedYet')}</div>
                                )}
                                {templates?.length > 0 && filteredTemplates?.length === 0 && (
                                    <div className="d-flex j-cc">{InovxLoader.t('facility:message.noTemplatesMatchSearch')}</div>
                                )}
                                {filteredTemplates?.length > 0 && (
                                    <div
                                        className={`${listMode === 'list' ? 'list-container' : 'thumbnails-container'
                                            }`}
                                    >
                                        {filteredTemplates.map(renderTemplateThumbnail)}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </React.Fragment>
        );
    };

    const renderListItem = (data: any) => {
        if (data.type === 'library' && data?.library?.Templates.length > 0) {
            return renderLibraryTemplate(data.library);
        }
        return null;
    };

    const handlePanelOpenChange = React.useCallback((open: boolean) => {
        setAssemblyLibraryBarOpen(open);
        FacilityActions.toggleAssemblyLibraryBar(open);
    }, []);

    const updateViewerState = () => {
        setSelectedTemplate(ViewerStore.getAssemblyCreationTemplate());
    };
    const updateFacilityState = () => {
        setSidebarOpen(FacilityStore.getSidebarOpen());
        setAssemblyLibraryBarOpen(FacilityStore.getAssemblyLibrarybarOpen());
    };
    const updateAssemblyState = () => {
        setAssemblyLibraries(AssemblyLibraryStore.getAssemblyLibrariesWithTemplates());
    };

    useEffect(() => {
        let AssemblyLibraryClass = '';
        if (sidebarOpen && assemblyLibraryBarOpen) {
            AssemblyLibraryClass = 'show-extend';
        } else if (assemblyLibraryBarOpen) {
            AssemblyLibraryClass = 'show';
        }
        setAssemblyLibraryClass(AssemblyLibraryClass);
    }, [sidebarOpen, assemblyLibraryBarOpen]);

    useEffect(() => {
        updateAssemblyLibraries();
        const handleFacilityStoreChange = () => updateFacilityState();
        const handleViewerStoreChange = () => updateViewerState();
        const handleAssemblyLibraryStoreChange = () => updateAssemblyState();

        FacilityStore.addChangeListener(handleFacilityStoreChange);
        ViewerStore.addChangeListener(handleViewerStoreChange);
        AssemblyLibraryStore.addChangeListener(handleAssemblyLibraryStoreChange);

        return () => {
            FacilityStore.removeChangeListener(handleFacilityStoreChange);
            ViewerStore.removeChangeListener(handleViewerStoreChange);
            AssemblyLibraryStore.removeChangeListener(handleAssemblyLibraryStoreChange);
        };
    }, []);

    const listDataSource = assemblyLibraries.map((library) => ({ type: 'library', library }));
    const filterAssemblyList = (e: any) => {
        setSearchKeyword(e.target.value)
    };
    const memoizedList = React.useMemo(() => {
        return listDataSource.map((data) => (
            <React.Fragment key={data.library.ID}>{renderListItem(data)}</React.Fragment> // Add unique key to React.Fragment
        ));
    }, [listDataSource, searchKeyword]);   

    const renderPanelBody = (panelContext: SessionPanelRenderContext) => (
        <>
            <SessionPanelToolbar
                context={panelContext}
                toolbarClassName="assembly-panel-toolbar actions"
                listClassName="assembly-panel-toolbar-list"
            >
                <li className="assembly-panel-action">
                    <button className={`no-btn ${listMode !== 'list' ? 'opacity-5' : ''}`} onClick={() => setListMode('list')} title={InovxLoader.t('facility:action.listView')}>
                        <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/icons/list.png`} alt={InovxLoader.t('facility:action.listView')} className="" />
                    </button>
                </li>
                <li className="assembly-panel-action">
                    <button className={`no-btn ${listMode !== 'thumbnail' ? 'opacity-5' : ''}`} onClick={() => setListMode('thumbnail')} title={InovxLoader.t('facility:action.thumbnailView')}>
                        <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/icons/thumbnail.png`} alt={InovxLoader.t('facility:action.thumbnailView')} className="" />
                    </button>
                </li>
            </SessionPanelToolbar>

            <div className="p-5px">
                <div className="search-box mt-5px">
                    <div className="search-addon">
                        <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/search_light.png`} alt="" />
                    </div>
                    <div className="field-col d-flex j-cc a-ic">
                        <input value={searchKeyword} onChange={(e) => filterAssemblyList(e)} placeholder={InovxLoader.t('facility:menu.search')} type="text" className="form-control" />
                    </div>
                    <div className="close-search">
                        {searchKeyword && <button type="button" className="no-btn d-flex j-cc a-ic" title={InovxLoader.t('facility:assetTagFilter.clear')} onClick={() => setSearchKeyword('')}>
                            <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/Tag/close.png`} alt={InovxLoader.t('facility:assetTagFilter.clear')} />
                        </button>}
                    </div>
                </div>
            </div>

            <SessionPanelMeasuredContent
                bottomPadding={10}
                className="list-view-container"
                fallbackOffset={98}
                minHeight={120}
            >
                {gridHeight => (
                    <div style={{ height: gridHeight, overflowY: 'auto' }}>
                        {memoizedList}
                    </div>
                )}
            </SessionPanelMeasuredContent>
        </>
    );

    return (
        <FacilitySlideoutSessionPanel
            attachedClassName={assemblyLibraryClass}
            className="assembly-library-bar"
            elementId="assembly-library-bar-content"
            isOpen={assemblyLibraryBarOpen}
            onOpenChange={handlePanelOpenChange}
            panelId={ASSEMBLY_LIBRARY_PANEL_ID}
            pinnedWidth={sidebarOpen ? 350 : 280}
        >
            {renderPanelBody}
        </FacilitySlideoutSessionPanel>
    );
});

export default AssemblyLibraryBar;